/**
 * Whether code in a project can add to Godot's glTF importer (`gltf_document.cpp:6731-6732`, `:6798-6804`). Before the
 * editor's first scan imports a file, it loads each GDExtension the scan finds, the autoloads and the enabled editor
 * plugins (`editor_file_system.cpp:310-345`). ADR-0043 says why only the project file and a listing can rule them out.
 */
import { declaredAutoloads, enabledEditorPlugins, parseProjectSettings, projectDataDirectoryPath, projectExtensionListPath, } from '../parser/projectSettingsParser.js';
import { GDEXTENSION_FILE_EXTENSION } from '../godot/project/editorScan.js';
import { PROJECT_FILE_PATH, extensionListEntries, extensionListPath } from '../godot/project/project.js';
import { StampedReads, loadOrNull, stampOf } from './stampedReads.js';
/**
 * Every `res://` path the probe may read: the project file, and the extension list under either data directory, since
 * which one it reads depends on the project file.
 */
export const PLUGIN_PROBE_PATHS = [
    PROJECT_FILE_PATH,
    extensionListPath(true),
    extensionListPath(false),
];
/** The facts of a `project.godot` the provider cannot deliver: it proves nothing, so code may extend the importer. */
const UNREAD_PROJECT = {
    mayRunCode: true,
    dataDirectory: projectDataDirectoryPath(null),
    listPath: projectExtensionListPath(null),
};
/** The answer for a provider that cannot list: it never rules a GDExtension out, so no file it reads can change it. */
const MAY_EXTEND = () => Promise.resolve(true);
/** The text of `path`, or null for a file the provider does not hold or cannot read. */
async function readText(provider, path) {
    const data = await loadOrNull(provider, path);
    if (data === null)
        return null;
    return typeof data === 'string' ? data : new TextDecoder().decode(data);
}
/** The facts of `project.godot`, or null, which a stamped read never keeps, for a file the provider cannot deliver. */
async function readProjectFacts(provider) {
    const text = await readText(provider, PROJECT_FILE_PATH);
    if (text === null)
        return null;
    const settings = parseProjectSettings(text);
    return {
        mayRunCode: enabledEditorPlugins(settings).length > 0 || declaredAutoloads(settings).length > 0,
        dataDirectory: projectDataDirectoryPath(settings),
        listPath: projectExtensionListPath(settings),
    };
}
/**
 * Whether the GDExtension list names an extension to load (`gdextension_manager.cpp:319-334`), or null, which a stamped
 * read never keeps, for a list the provider cannot deliver.
 */
async function readListHasEntries(provider, path) {
    const text = await readText(provider, path);
    return text === null ? null : extensionListEntries(text).length > 0;
}
/**
 * Whether the project may hold a GDExtension the scan loads: true unless the provider lists the project and finds
 * none outside `dataDirectory`. A provider that cannot list, or whose listing rejects, proves nothing.
 */
async function mayHoldGdextension(provider, dataDirectory) {
    if (!provider.listFiles)
        return true;
    const listed = await provider.listFiles(GDEXTENSION_FILE_EXTENSION).catch(() => null);
    if (listed === null)
        return true;
    return listed.some((path) => !path.startsWith(`${dataDirectory}/`));
}
export class ProjectPluginProbes {
    projects = new StampedReads();
    lists = new StampedReads();
    /**
     * Starts reading the project files behind `provider` at once, and returns the answer. The project file and the
     * extension list are stamped in parallel, and one whose stamp is unchanged is neither read nor parsed again. The
     * answer lists the project only when those files leave it open. A provider that cannot list reads nothing.
     */
    probe(provider) {
        if (!provider.listFiles)
            return MAY_EXTEND;
        const evidence = this.readEvidence(provider);
        // Awaited only when the answer is asked for: the await there still throws, and an unawaited rejection stays silent.
        evidence.catch(() => undefined);
        return async () => {
            const { mayRunCode, dataDirectory, listsExtension } = await evidence;
            return mayRunCode || listsExtension || mayHoldGdextension(provider, dataDirectory);
        };
    }
    async readEvidence(provider) {
        const keptListPath = this.projects.peek(provider, PROJECT_FILE_PATH)?.listPath;
        const keptListStamp = keptListPath === undefined ? undefined : stampOf(provider, keptListPath);
        const project = (await this.projects.get(provider, PROJECT_FILE_PATH, () => readProjectFacts(provider))) ??
            UNREAD_PROJECT;
        if (project.mayRunCode)
            return { ...project, listsExtension: false };
        const { listPath } = project;
        const listStamp = listPath === keptListPath ? keptListStamp : undefined;
        const listsExtension = await this.lists.get(provider, listPath, () => readListHasEntries(provider, listPath), listStamp);
        return { ...project, listsExtension: listsExtension ?? false };
    }
}
//# sourceMappingURL=projectPlugins.js.map